import '@/styles/globals.css'

import {
  Notification,
  NotificationContextProvider,
} from '@/app/components/Notification'
import useCustomFonts from '@/app/hooks/useCustomFonts'
import { getQueryClient } from '@/app/lib/query-client'
import { useScrollRestoration } from '@/app/lib/scroll-restoration'
import { GoogleAnalytics } from '@/app/modules/shared/components/google-analytics'
import { GoogleTagManager } from '@/app/modules/shared/components/google-tag-manager/GoogleTagManager'
import { HelpScoutChatBubble } from '@/app/modules/shared/components/help-scout-chat-bubble'
import { LanguageSelectionProvider } from '@/app/modules/shared/providers/LanguageSelectionProvider'
import { AuthProvider } from '@/app/providers/Auth/Auth'
import { MARKETPLACE_THEME } from '@/styles/mui-theme/theme'
import { CssBaseline, StyledEngineProvider, ThemeProvider } from '@mui/material'
import { AppCacheProvider } from '@mui/material-nextjs/v14-pagesRouter'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { NextIntlClientProvider } from 'next-intl'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import PagesTopLoader from 'nextjs-toploader'
import { QueryClientProvider } from 'react-query'
import resolveConfig from 'tailwindcss/resolveConfig'
import myConfig from '../../tailwind.config'

const ReactQueryDevtools = dynamic(() =>
  import('react-query/devtools').then((mod) => mod.ReactQueryDevtools)
)

export default function MydraMarketplace({
  Component,
  pageProps,
  router,
  locale: pageLocale,
}) {
  const localeSet = pageLocale || router.locale || 'en'

  const { theme } = resolveConfig(myConfig)
  const queryClient = getQueryClient(router)

  const { inter, planarGt } = useCustomFonts()

  useScrollRestoration(router)

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

  return (
    <AppCacheProvider>
      <StyledEngineProvider injectFirst>
        <QueryClientProvider client={queryClient}>
          <NextIntlClientProvider
            locale={localeSet}
            messages={pageProps.messages}
            timeZone={timeZone}
            onError={() => {
              // ignore errors
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <AuthProvider>
                <LanguageSelectionProvider>
                  <div
                    className={`${inter.variable} ${planarGt.variable} mydra-base-layout flex min-h-screen flex-col`}
                  >
                    <Head>
                      <title>Mydra | Courses Marketplace</title>
                      <meta
                        name="description"
                        content="We've mapped the most in-demand skills, allowing to you stay ahead of the game.
          With our support, you'll not only prepare for the future; you'll lead it."
                        key="desc"
                      />
                      <link
                        rel="apple-touch-icon"
                        sizes="180x180"
                        href="/apple-touch-icon.png"
                      />
                      <link
                        rel="icon"
                        type="image/png"
                        sizes="32x32"
                        href="/favicon-32x32.png"
                      />
                      <link
                        rel="icon"
                        type="image/png"
                        sizes="16x16"
                        href="/favicon-16x16.png"
                      />
                      <link rel="manifest" href="/site.webmanifest" />
                      <link
                        rel="mask-icon"
                        href="/safari-pinned-tab.svg"
                        color="#5bbad5"
                      />
                      <meta name="msapplication-TileColor" content="#da532c" />
                      <meta name="theme-color" content="#ffffff" />
                      <meta
                        name="viewport"
                        content="width=device-width, initial-scale=1, maximum-scale=1"
                      />
                    </Head>
                    <style jsx global>{`
                      html {
                        font-family: ${inter.style.fontFamily};
                      }
                    `}</style>

                    <GoogleTagManager />
                    <GoogleAnalytics />
                    <HelpScoutChatBubble />

                    <ThemeProvider theme={MARKETPLACE_THEME}>
                      <CssBaseline />
                      <NotificationContextProvider>
                        <PagesTopLoader color={theme.colors['mydra-purple']} />
                        <Component {...pageProps} />
                        <Notification />
                      </NotificationContextProvider>
                    </ThemeProvider>
                  </div>
                </LanguageSelectionProvider>
              </AuthProvider>
            </LocalizationProvider>
          </NextIntlClientProvider>
          {process.env.NODE_ENV === 'development' && <ReactQueryDevtools />}
        </QueryClientProvider>
      </StyledEngineProvider>
    </AppCacheProvider>
  )
}
